import React, {useEffect, useState, useRef} from 'react';
import "../css/GenericPage.css";
import "../css/DashboardV2.css"
import {FlexCol, FlexRow, StandardPage} from "../components/generic/Containers";
import {BCHeader5, Body1, EnhancedPageTitle, useContentPageSpring} from "../components/generic/BCHeaders";
import {BCIcon, ICONS} from "../components/generic/BCIcons";
import LogoMedium from "../images/Logo_Medium.webp";
import {BCPrimaryButton, BCTertiaryButton} from "../components/generic/BCButtons";
import {BlogMainMenu, ChallengesMainMenu, CoursesMainMenu, GamesMainMenu, ReferencesMainMenu, ResourceMainMenu, ToolsMainMenu, VideosMainMenu, DashboardMainMenu} from "../components/DashboardMainMenus";
import {AccountContent, DashboardContent, LegalContent} from "../components/DashboardContent";
import {useGlobalState} from "../GlobalState";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import {trackEvent, TRACKING_EVENTS} from "../util/TrackingHelper";
import {getContentFromParams} from "../util/ContentHelper";
import {scrollToTop} from "../util/ScrollToTop";
import {animated} from "@react-spring/web";
import {ALL_CONTENT} from "../constants/LearningPathsConfig";
import {LandingPage} from "./LandingPage";

const ContentPageContainer: React.FC<{
    pageName: string;
    getPageTitle: (pageName: string) => string;
    addToStack: (newId: string, type: string) => void;
}> = ({ pageName, getPageTitle, addToStack }) => {
    const contentPageSpring = useContentPageSpring();

    const getPageSubtitle = (pageName: string): string => {
        switch (pageName) {
            case DASHBOARD_PAGES.COURSES:
                return "Master programming fundamentals and advance your tech career";
            case DASHBOARD_PAGES.VIDEOS:
                return "Learn from real developers and practice with mock interviews";
            case DASHBOARD_PAGES.RESOURCES:
                return "Essential guides, cheat sheets, and resume examples";
            case DASHBOARD_PAGES.TOOLS:
                return ALL_CONTENT.allAITools[0]?.subtitle || "Leverage AI to enhance your job search and career growth";
            case DASHBOARD_PAGES.BLOG:
                return ALL_CONTENT.allBlogs[0]?.subtitle || "Insights, tips, and stories from the tech industry";
            case DASHBOARD_PAGES.REFERENCES:
                return "Curated resources and references for your programming journey";
            case DASHBOARD_PAGES.CHALLENGES:
                return "Mathematical and algorithmic challenges to sharpen your problem-solving skills";
            case DASHBOARD_PAGES.GAMES:
                return "Interactive games and challenges to improve your programming skills";
            case DASHBOARD_PAGES.ACCOUNT:
                return "Manage your account settings and preferences";
            case DASHBOARD_PAGES.LEGAL:
                return "";
            default:
                return "How'd You Get Here..?";
        }
    };

    return (
        <animated.div
            style={{
                ...contentPageSpring,
                display: 'flex',
                flexDirection: 'column',
                flex: 1
            }}
        >
            <FlexCol padding={2} pBottom={4} className={"dashboard-content-container"}>
                <EnhancedPageTitle title={getPageTitle(pageName)} subtitle={getPageSubtitle(pageName)}/>
                {pageName === DASHBOARD_PAGES.COURSES && <CoursesMainMenu setSelectedContent={addToStack}/>}
                {pageName === DASHBOARD_PAGES.VIDEOS && <VideosMainMenu setSelectedContent={addToStack}/>}
                {pageName === DASHBOARD_PAGES.TOOLS && <ToolsMainMenu setSelectedContent={addToStack}/>}
                {pageName === DASHBOARD_PAGES.RESOURCES && <ResourceMainMenu setSelectedContent={addToStack}/>}
                {pageName === DASHBOARD_PAGES.REFERENCES && <ReferencesMainMenu setSelectedContent={addToStack}/>}
                {pageName === DASHBOARD_PAGES.CHALLENGES && <ChallengesMainMenu setSelectedContent={addToStack}/>}
                {pageName === "challenge-detail" && <></>}
                {pageName === DASHBOARD_PAGES.GAMES && <GamesMainMenu setSelectedContent={addToStack}/>}
                {pageName === DASHBOARD_PAGES.BLOG && <BlogMainMenu setSelectedContent={addToStack}/>}
            </FlexCol>
        </animated.div>
    );
};

export const DASHBOARD_PAGES = {
    HOME: "",
    ROADMAP: "roadmap",
    COURSES: "courses",
    VIDEOS: "videos",
    RESOURCES: "resources",
    REFERENCES: "references",
    CHALLENGES: "challenges",
    GAMES: "games",
    TOOLS: "tools",
    FORUMS: "forums",
    BLOG: "blog",
    ACCOUNT: "account",
    LEGAL: "legal"
}

const headerOptions = [
    { text: "Home", icon: ICONS.HOME, page: DASHBOARD_PAGES.HOME, authNeeded: false, inMore: false },
    { text: "Courses", icon: ICONS.LESSONS, page: DASHBOARD_PAGES.COURSES, authNeeded: false, inMore: false },
    { text: "Videos", icon: ICONS.VIDEO, page: DASHBOARD_PAGES.VIDEOS, authNeeded: false, inMore: false },
    { text: "Resources", icon: ICONS.PDF, page: DASHBOARD_PAGES.RESOURCES, authNeeded: false, inMore: false },
    { text: "References", icon: ICONS.REFERENCES_SEARCH, page: DASHBOARD_PAGES.REFERENCES, authNeeded: false, inMore: false },
    { text: "Challenges", icon: ICONS.CHALLENGES, page: DASHBOARD_PAGES.CHALLENGES, authNeeded: false, inMore: false },
    { text: "Tools", icon: ICONS.SPARKLE, page: DASHBOARD_PAGES.TOOLS, authNeeded: false, inMore: false },
    // { text: "Forums", icon: ICONS.PEN, page: DASHBOARD_PAGES.FORUMS, authNeeded: false, inMore: false },
    { text: "Blog", icon: ICONS.PEN, page: DASHBOARD_PAGES.BLOG, authNeeded: false, inMore: false },
    { text: "Account", icon: ICONS.ACCOUNT, page: DASHBOARD_PAGES.ACCOUNT, authNeeded: true, inMore: true },
    { text: "Legal Info", icon: ICONS.ACCOUNT, page: DASHBOARD_PAGES.LEGAL, authNeeded: false, inMore: true }
];

const mobileHeaderOptions = [
    { text: "Home", icon: ICONS.HOME, page: DASHBOARD_PAGES.HOME, authNeeded: false, inMore: false },
    { text: "Courses", icon: ICONS.LESSONS, page: DASHBOARD_PAGES.COURSES, authNeeded: false, inMore: false },
    { text: "Videos", icon: ICONS.VIDEO, page: DASHBOARD_PAGES.VIDEOS, authNeeded: false, inMore: false },
    { text: "Resources", icon: ICONS.PDF, page: DASHBOARD_PAGES.RESOURCES, authNeeded: false, inMore: false },
    { text: "References", icon: ICONS.REFERENCES_SEARCH, page: DASHBOARD_PAGES.REFERENCES, authNeeded: false, inMore: false },
    { text: "Challenges", icon: ICONS.CHALLENGES, page: DASHBOARD_PAGES.CHALLENGES, authNeeded: false, inMore: true },
    { text: "Tools", icon: ICONS.SPARKLE, page: DASHBOARD_PAGES.TOOLS, authNeeded: false, inMore: true },
    // { text: "Forums", icon: ICONS.PEN, page: DASHBOARD_PAGES.FORUMS, authNeeded: false, inMore: true },
    { text: "Blog", icon: ICONS.PEN, page: DASHBOARD_PAGES.BLOG, authNeeded: false, inMore: true },
    { text: "Account", icon: ICONS.ACCOUNT, page: DASHBOARD_PAGES.ACCOUNT, authNeeded: true, inMore: true },
    { text: "Legal Info", icon: ICONS.ACCOUNT, page: DASHBOARD_PAGES.LEGAL, authNeeded: false, inMore: true }
];

export const DashboardPageV2: React.FC<any> = () => {
    const {page, contentId, subcontentId} = useParams();
    const { userProgressData, loadingUserProgress, isAuthenticated, isMobile, setShowPremiumOverlay, isPremium, setShowAuthModal, setAuthModalMode, user } = useGlobalState();
    const navigate = useNavigate();
    
    // Navigation stack for proper back navigation
    const [navigationStack, setNavigationStack] = useState<string[]>([]);
    const location = useLocation();

    const onMenuChanged = (newIndex: number, isMobileMenu: boolean = false) => {
        const options = isMobileMenu ? mobileHeaderOptions : headerOptions;
        const selectedItem = options[newIndex];
        const targetPage = selectedItem.page;
        
        // Track navigation for browsing intent
        trackEvent(TRACKING_EVENTS.PAGE_NAVIGATION, {
            content_id: targetPage 
        });
        
        navigate(`/${targetPage}`);
        // Scroll to top when changing pages
        scrollToTop();
    }

    // Function to get page title for mobile
    const getPageTitle = (pageName: string): string => {
        const pageOption = headerOptions.find(option => option.page === pageName);
        if (pageOption) {
            return pageOption.text;
        }

        return "404";
    };

    // Check onboarding status early
    const [checkingOnboarding, setCheckingOnboarding] = useState(true);
    
    useEffect(() => {
        if (loadingUserProgress) return;
        
        // Check if user needs onboarding
        if (user && userProgressData) {
            if (!userProgressData.hasOnboarded) {
                navigate('/onboarding');
            } else {
                setCheckingOnboarding(false);
            }
        } else if (!user) {
            // No user logged in, can show dashboard
            setCheckingOnboarding(false);
        }
    }, [loadingUserProgress, user, userProgressData, navigate]);

    // Track navigation for stack-based back navigation
    useEffect(() => {
        const currentPath = location.pathname;
        setNavigationStack((prev: string[]) => {
            // Don't add duplicates of the same path
            if (prev[prev.length - 1] === currentPath) {
                return prev;
            }
            // Add current path to stack
            return [...prev, currentPath];
        });
    }, [location.pathname]);

    // Handle challenge detail routing - when we have both contentId and subcontentId for challenges
    const isDetailPage = page === "challenges" && contentId && subcontentId;
    
    let pageName = page ? page : "";
    let content: any = null;
    
    if (isDetailPage) {
        // For challenge detail pages: /challenges/mathematical-challenges/sum-of-multiples
        const challengePath = `${contentId}/${subcontentId}`; // mathematical-challenges/sum-of-multiples
        content = getContentFromParams("challenge-detail", challengePath);
        pageName = "challenges"; // Keep main page as challenges for proper navigation
    } else if (contentId) {
        // Regular content pages: /challenges/mathematical-challenges
        content = getContentFromParams(pageName, contentId);
    }
    
    // Redirect non-premium users to home when accessing specific item pages (except blog)
    useEffect(() => {
        if (content && pageName !== DASHBOARD_PAGES.BLOG) {
            if (!isAuthenticated || !isPremium) {
                navigate(`/`)
            }
        }
    }, [content, isAuthenticated, isPremium, pageName, navigate]);

    // Show PremiumOverlay for logged-in, onboarded, non-premium users on load
    useEffect(() => {
        if (!loadingUserProgress && user && userProgressData?.hasOnboarded && !isPremium) {
            setShowPremiumOverlay(true);
        }
    }, [loadingUserProgress, user, userProgressData, isPremium, setShowPremiumOverlay]);

    useEffect(() => {
        if (content && !isAuthenticated) {
            // Allow blog posts, references, challenges, and games to be accessed without authentication
            if (pageName !== DASHBOARD_PAGES.BLOG && pageName !== DASHBOARD_PAGES.REFERENCES && pageName !== DASHBOARD_PAGES.CHALLENGES && pageName !== DASHBOARD_PAGES.GAMES) {
                // Redirect to the root page of the content type
                navigate(`/${pageName}`);
            }
        }
    }, [content, isAuthenticated, pageName, navigate]);

    const addToStack = (newId: string, type: string) => {
        // Check if user is not authenticated and trying to access protected content
        if (!isAuthenticated) {
            // Show register modal for courses, videos, resources, and tools (but not blog, references, challenges, or games)
            if (pageName === DASHBOARD_PAGES.COURSES || 
                pageName === DASHBOARD_PAGES.VIDEOS || 
                pageName === DASHBOARD_PAGES.RESOURCES ||
                pageName === DASHBOARD_PAGES.TOOLS) {
                setAuthModalMode('register');
                setShowAuthModal(true);
                return;
            }
        }
        
        // Show premium overlay for all content except blog previews
        const nextContent = getContentFromParams(pageName, newId)
        if (pageName !== DASHBOARD_PAGES.BLOG && (!isAuthenticated || !isPremium)) {
            setShowPremiumOverlay(true)
        } else {
            // Track content item click
            trackEvent(TRACKING_EVENTS.CONTENT_ITEM_CLICKED, {
                content_type: pageName,
                content_id: newId,
                content_name: nextContent?.name || newId,
                source: 'content_list'
            });
            
            navigate(`/${page}/${newId}`);
            // Scroll to top when navigating to new content
            scrollToTop();
        }
    };
    const handleBack = () => {
        setNavigationStack((prev: string[]) => {
            // Remove current page from stack
            const newStack = [...prev];
            newStack.pop(); // Remove current page
            
            if (newStack.length > 0) {
                // Go to previous page in stack
                const previousPage = newStack[newStack.length - 1];
                navigate(previousPage);
                return newStack;
            } else {
                // Fallback to main page
                navigate(`/${page}`);
                return [];
            }
        });
        // Scroll to top when going back
        scrollToTop();
    };

    if (!isAuthenticated) return (
        <LandingPage navigate={navigate}/>
    )

    // Show loading state while checking onboarding status for authenticated users
    if (isAuthenticated && checkingOnboarding) {
        return (
            <StandardPage fullscreen={true} className={"dashboard-v2-container"}>
                <FlexCol align="center" justify="center" style={{ minHeight: '100vh' }}>
                    <Body1 text="Loading..." />
                </FlexCol>
            </StandardPage>
        );
    }

    return (
        <StandardPage fullscreen={true} className={"dashboard-v2-container"}>
            <FlexRow widthPercent={100}>
                {!isMobile && <DashboardLeftMenu currentPage={pageName} onMenuChanged={onMenuChanged} isAuthenticated={isAuthenticated} />}
                <FlexCol widthPercent={100} className={"dashboard-content-scrollable"}>
                    {isMobile && <MobileDashboardHeader/>}
                    {!content && pageName !== DASHBOARD_PAGES.HOME && !isDetailPage &&
                        <ContentPageContainer
                            key={pageName}
                            pageName={pageName}
                            getPageTitle={getPageTitle}
                            addToStack={addToStack}
                        />
                    }
                    {!content && pageName === DASHBOARD_PAGES.HOME &&
                        <FlexCol className={"dashboard-content-container"}>
                            {pageName === DASHBOARD_PAGES.HOME && <DashboardMainMenu setSelectedContent={addToStack} navigate={navigate} isEducator={false}/> }
                        </FlexCol>
                    }
                    {(content || isDetailPage) &&
                        <DashboardContent page={isDetailPage ? "challenge-detail" : pageName} handleBack={handleBack} selectedContent={content} setSelectedContent={addToStack}/>
                    }
                    {page === DASHBOARD_PAGES.ACCOUNT && <AccountContent /> }
                    {page === DASHBOARD_PAGES.LEGAL && <LegalContent /> }
                    {isMobile && <DashboardMobileBottomMenu currentPage={pageName} onMenuChanged={onMenuChanged} isAuthenticated={isAuthenticated}/>}
                </FlexCol>
            </FlexRow>
        </StandardPage>
    );
};

export const DashboardLeftMenu: React.FC<any> = ({currentPage, onMenuChanged, isAuthenticated}) => {
    const [isHovered, setIsHovered] = useState(false);
    const navigate = useNavigate();
    const { setShowAuthModal, setAuthModalMode, isPremium, setShowPremiumOverlay } = useGlobalState();
    
    const handleAuthClick = (mode: 'login' | 'register') => {
        // Track which auth button was clicked
        if (mode === 'login') {
            trackEvent(TRACKING_EVENTS.LOGIN_CLICKED, {
                source: 'desktop_header'
            });
        } else {
            trackEvent(TRACKING_EVENTS.REGISTER_CLICKED, {
                source: 'desktop_header'
            });
        }
        
        setAuthModalMode(mode);
        setShowAuthModal(true);
    };
    
    
    const onHeaderLinkClicked = (navUrl: string) => {
        navigate(navUrl)
    }
    
    return (
        <FlexCol className={"dashboard-left-menu-container"}>
            <FlexCol heightPercent={100}>
                <img 
                    className={"header-logo"} 
                    src={LogoMedium} 
                    alt={"Beyond Code Logo"} 
                    onClick={() => onHeaderLinkClicked("/")}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => e.key === 'Enter' && onHeaderLinkClicked("/")}
                    style={{ cursor: 'pointer' }}
                />
                {headerOptions
                    .filter(item => (!item.authNeeded || (item.authNeeded && isAuthenticated)) && !item.inMore)
                    .map((item, filteredIndex) => {
                        const originalIndex = headerOptions.indexOf(item);
                        return (
                            <React.Fragment key={`header-option-${originalIndex}`}>
                                {filteredIndex > 0 && (
                                    <div key={`divider-${originalIndex}`} className="dashboard-item-divider" />
                                )}
                                <FlexRow 
                                    key={`menu-item-${originalIndex}`}
                                    className={(currentPage === item.page) ? "dashboard-item selected" : "dashboard-item"} 
                                    gap={1}
                                    onClick={() => onMenuChanged(originalIndex)}
                                >
                                    <BCIcon icon={item.icon}/>
                                    <BCHeader5 text={item.text}/>
                                </FlexRow>
                            </React.Fragment>
                        );
                    })}

                <div className="dashboard-item-divider" />
                <div className={"dashboard-item more"} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
                    <FlexRow gap={1} align={"center"}>
                        <BCIcon icon={ICONS.MORE}/>
                        <BCHeader5 text={"More"}/>
                    </FlexRow>
                    {isHovered && (
                        <div className="dashboard-dropdown-menu">
                            {headerOptions
                                .filter(item => (!item.authNeeded || (item.authNeeded && isAuthenticated)) && item.inMore)
                                .map((item) => {
                                    const originalIndex = headerOptions.indexOf(item);
                                    return (
                                        <FlexRow 
                                            key={`dropdown-option-${originalIndex}`}
                                            className={"dashboard-item more-option"}
                                            onClick={() => onMenuChanged(originalIndex)}
                                        >
                                            <BCHeader5 text={item.text}/>
                                        </FlexRow>
                                    );
                                })}
                        </div>
                    )}
                </div>
            </FlexCol>

            {!isAuthenticated && <FlexCol gap={.5} margin={1}>
                <BCTertiaryButton text={"Log In"} onClick={() => handleAuthClick('login')}/>
                <BCPrimaryButton text={"Register"} onClick={() => handleAuthClick('register')}/>
            </FlexCol>}
            

        </FlexCol>
    );
};

export const DashboardMobileBottomMenu: React.FC<any> = ({ currentPage, onMenuChanged, isAuthenticated }) => {
    const [showMore, setShowMore] = useState(false);
    const [visibleItems, setVisibleItems] = useState<any[]>([]);
    const [overflowItems, setOverflowItems] = useState<any[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    // Calculate how many items can fit in the available space
    const calculateVisibleItems = () => {
        const availableOptions = mobileHeaderOptions.filter(item => 
            (!item.authNeeded || (item.authNeeded && isAuthenticated))
        );
        
        // Separate items that should always be in main menu vs overflow menu
        const mainMenuCandidates = availableOptions.filter(item => !item.inMore);
        const alwaysInOverflow = availableOptions.filter(item => item.inMore);
        
        const container = containerRef.current;
        if (!container) {
            // Fallback if container not available yet
            setVisibleItems(mainMenuCandidates);
            setOverflowItems(alwaysInOverflow);
            return;
        }

        const containerWidth = container.offsetWidth;
        const moreButtonWidth = 60; // Approximate width of the "More" button
        const itemWidth = 60; // Approximate width of each menu item
        const availableWidth = containerWidth - moreButtonWidth;
        
        // Calculate how many items can fit
        const maxVisibleItems = Math.floor(availableWidth / itemWidth);
        
        // Ensure we don't exceed the actual number of main menu candidates
        const actualVisibleCount = Math.min(maxVisibleItems, mainMenuCandidates.length);
        
        // Split main menu candidates into visible and overflow
        const visible = mainMenuCandidates.slice(0, actualVisibleCount);
        const dynamicOverflow = mainMenuCandidates.slice(actualVisibleCount);
        
        // Combine dynamic overflow with always-in-overflow items
        const allOverflow = [...dynamicOverflow, ...alwaysInOverflow];
        
        setVisibleItems(visible);
        setOverflowItems(allOverflow);
    };

    // Recalculate on mount and window resize
    useEffect(() => {
        // Small delay to ensure container is fully rendered
        const timer = setTimeout(() => {
            calculateVisibleItems();
        }, 100);
        
        const handleResize = () => {
            calculateVisibleItems();
        };
        
        window.addEventListener('resize', handleResize);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', handleResize);
        };
    }, [isAuthenticated]);

    const toggleMoreMenu = () => setShowMore(prev => !prev);

    const handleItemClick = (item: any, index: number) => {
        // Find the original index in mobileHeaderOptions
        const originalIndex = mobileHeaderOptions.findIndex(opt => opt.page === item.page);
        onMenuChanged(originalIndex, true);
    };

    const handleOverflowItemClick = (item: any) => {
        // Find the original index in mobileHeaderOptions
        const originalIndex = mobileHeaderOptions.findIndex(opt => opt.page === item.page);
        onMenuChanged(originalIndex, true);
        setShowMore(false);
    };

    return (
        <FlexCol className="dashboard-mobile-bottom-menu-container">
            {showMore && overflowItems.length > 0 && (
                <div className="dashboard-dropdown-menu-mobile">
                    {overflowItems.map((item, index) => (
                        <FlexRow key={index} className="dashboard-item more-option" onClick={() => handleOverflowItemClick(item)}>
                            <BCHeader5 text={item.text} />
                        </FlexRow>
                    ))}
                </div>
            )}
            <div ref={containerRef} className="dashboard-mobile-bottom-menu">
                {visibleItems.map((item, index) => (
                    <div
                        key={index}
                        className={`dashboard-mobile-bottom-menu-item ${(item.page === currentPage) ? 'selected' : ''}`}
                        onClick={() => handleItemClick(item, index)}
                    >
                        <BCIcon icon={item.icon} />
                    </div>
                ))}
                {overflowItems.length > 0 && (
                    <div className="dashboard-mobile-bottom-menu-item more-menu-item" onClick={toggleMoreMenu}>
                        <BCIcon icon={ICONS.MORE} />
                    </div>
                )}
            </div>
        </FlexCol>
    );
};

export const MobileDashboardHeader: React.FC<any> = ({}) => {
    const navigate = useNavigate()
    const { isAuthenticated, setShowAuthModal, setAuthModalMode, isPremium, setShowPremiumOverlay } = useGlobalState();
    
    const onHeaderLinkClicked = (navUrl: string) => {
        navigate(navUrl)
        trackEvent(TRACKING_EVENTS.PAGE_NAVIGATION, {
            content_id: "/" 
        })
    }

    const handleAuthClick = (mode: 'login' | 'register') => {
        // Track which auth button was clicked
        if (mode === 'login') {
            trackEvent(TRACKING_EVENTS.LOGIN_CLICKED, {
                source: 'mobile_header'
            });
        } else {
            trackEvent(TRACKING_EVENTS.REGISTER_CLICKED, {
                source: 'mobile_header'
            });
        }
        
        setAuthModalMode(mode);
        setShowAuthModal(true);
    };
    

    return (
        <div className="dashboard-mobile-header-module-container">
            <div className={"header-module-content"}>
                <img
                    className={"header-logo"}
                    src={LogoMedium}
                    alt={"Beyond Code Logo"}
                    onClick={() => onHeaderLinkClicked("/")}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => e.key === 'Enter' && onHeaderLinkClicked("/")}
                />
                {isAuthenticated && isPremium &&
                    <BCIcon
                        icon={ICONS.ACCOUNT}
                        className={"header-account-icon"}
                        onClick={() => onHeaderLinkClicked("/account")}
                        aria-label="My Account"
                        role="button"
                        tabIndex={0}/>
                }
                {!isAuthenticated && (
                    <div className={'header-logged-out-buttons'}>
                        <div className={'header-logged-out-web-container'}>
                            <button className="log-in-button" onClick={() => handleAuthClick('login')}>Login</button>
                            <button className="register-button" onClick={() => handleAuthClick('register')}>Sign Up</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export const DashboardNavBar: React.FC<any> = ({onNavClicked}) => {
    return (
        <BCTertiaryButton className={"dashboard-nav-back"} iconLeft={ICONS.LEFT_ARROW} text={"Back"} onClick={onNavClicked} flexShrink={true} alignSelf={"flex-start"}/>
    );
};